import { VStack, Spinner, Center, Text, Button, useColorModeValue, Stack, Box, HStack } from '@chakra-ui/react';
import { RepeatIcon, ArrowUpIcon } from '@chakra-ui/icons';
import { useNavigate } from 'react-router-dom';
import ContentRow from '../components/ContentRow';
import HeroBanner from '../components/HeroBanner';
import AdBanner from '../components/AdBanner';
import AdSenseSlot from '../components/AdSenseSlot';
import { useHomeContent } from '../contexts/UnifiedContentContext';
import { useAuth } from '../contexts/AuthContext';
import { ADSENSE_CLIENT_ID, ADSENSE_SLOTS } from '../constants/adsense';
import useAdFree from '../hooks/useAdFree';
import { useEffect } from 'react';

const Home: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { isAdFree, isLoading: isAdFreeLoading } = useAdFree();
  
  // Get content from optimized home hook
  const {
    popularMovies,
    popularSeries,
    popularShorts,
    popularBooks,
    popularComics,
    bestRatedMovies,
    bestRatedSeries,
    bestRatedShorts,
    bestRatedBooks,
    bestRatedComics,
    latestMovies,
    latestSeries,
    latestShorts,
    latestBooks,
    latestComics,
    hiddenGemsMovies,
    hiddenGemsSeries,
    hiddenGemsShorts,
    hiddenGemsBooks,
    hiddenGemsComics,
    loading: isLoading,
    loadAllContent
  } = useHomeContent(user?.uid);

  // Load all content on mount - only once
  useEffect(() => {
    loadAllContent();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Empty deps - only run on mount

  // Color mode values
  const titleColor = useColorModeValue('gray.900', 'white');
  const labelColor = useColorModeValue('gray.600', 'gray.400');
  const emptyStateBg = useColorModeValue('whiteAlpha.700', 'whiteAlpha.100');
  const sectionBg = useColorModeValue('purple.100', 'purple.900');
  const sectionTextColor = useColorModeValue('gray.800', 'white');
  const sectionLabelColor = useColorModeValue('gray.600', 'gray.300');
  const cardBg = useColorModeValue('white', 'gray.800');
  const sectionBorderColor = useColorModeValue('purple.200', 'purple.700');

  // Transform content data for ContentRow component
  const transformContentForRow = (contentList: any[], title: string, type: string = 'movie') => ({
    title,
    items: contentList.slice(0, 6).map(item => ({
      id: item.id,
      title: item.title,
      type: type,
      thumbnail: item.thumbnail || ''
    }))
  });

  // Build content rows - organized by content type, then by category
  const contentRows = [
    // Most Popular rows
    { ...transformContentForRow(popularMovies, 'Most Popular Movies', 'movie'), isContinueWatching: false, isSubscribed: false },
    { ...transformContentForRow(popularSeries, 'Most Popular Series', 'series'), isContinueWatching: false, isSubscribed: false },
    { ...transformContentForRow(popularShorts, 'Most Popular Shorts', 'short'), isContinueWatching: false, isSubscribed: false },
    { ...transformContentForRow(popularBooks, 'Most Popular Books', 'book'), isContinueWatching: false, isSubscribed: false },
    { ...transformContentForRow(popularComics, 'Most Popular Comics', 'comic'), isContinueWatching: false, isSubscribed: false },
    
    // Best Rated rows
    { ...transformContentForRow(bestRatedMovies, 'Best Rated Movies', 'movie'), isContinueWatching: false, isSubscribed: false },
    { ...transformContentForRow(bestRatedSeries, 'Best Rated Series', 'series'), isContinueWatching: false, isSubscribed: false },
    { ...transformContentForRow(bestRatedShorts, 'Best Rated Shorts', 'short'), isContinueWatching: false, isSubscribed: false },
    { ...transformContentForRow(bestRatedBooks, 'Best Rated Books', 'book'), isContinueWatching: false, isSubscribed: false },
    { ...transformContentForRow(bestRatedComics, 'Best Rated Comics', 'comic'), isContinueWatching: false, isSubscribed: false },
    
    // Latest rows
    { ...transformContentForRow(latestMovies, 'Latest Movies', 'movie'), isContinueWatching: false, isSubscribed: false },
    { ...transformContentForRow(latestSeries, 'Latest Series', 'series'), isContinueWatching: false, isSubscribed: false },
    { ...transformContentForRow(latestShorts, 'Latest Shorts', 'short'), isContinueWatching: false, isSubscribed: false },
    { ...transformContentForRow(latestBooks, 'Latest Books', 'book'), isContinueWatching: false, isSubscribed: false },
    { ...transformContentForRow(latestComics, 'Latest Comics', 'comic'), isContinueWatching: false, isSubscribed: false },
    
    // Hidden Gems rows (only show if there's content)
    ...(hiddenGemsMovies.length > 0 ? [{ ...transformContentForRow(hiddenGemsMovies, 'Hidden Gems Movies', 'movie'), isContinueWatching: false, isSubscribed: false }] : []),
    ...(hiddenGemsSeries.length > 0 ? [{ ...transformContentForRow(hiddenGemsSeries, 'Hidden Gems Series', 'series'), isContinueWatching: false, isSubscribed: false }] : []),
    ...(hiddenGemsShorts.length > 0 ? [{ ...transformContentForRow(hiddenGemsShorts, 'Hidden Gems Shorts', 'short'), isContinueWatching: false, isSubscribed: false }] : []),
    ...(hiddenGemsBooks.length > 0 ? [{ ...transformContentForRow(hiddenGemsBooks, 'Hidden Gems Books', 'book'), isContinueWatching: false, isSubscribed: false }] : []),
    ...(hiddenGemsComics.length > 0 ? [{ ...transformContentForRow(hiddenGemsComics, 'Hidden Gems Comics', 'comic'), isContinueWatching: false, isSubscribed: false }] : []),
  ].filter(row => row.items.length > 0); // Only show rows with content

  // Check if any content exists
  const isContentEmpty = contentRows.length === 0;

  const handleSeeAll = (title: string) => {
    const routeMap: { [key: string]: string } = {
      // Popular
      'Most Popular Movies': '/movies',
      'Most Popular Series': '/series',
      'Most Popular Shorts': '/shorts',
      'Most Popular Books': '/books',
      'Most Popular Comics': '/comics',
      // Best Rated
      'Best Rated Movies': '/movies',
      'Best Rated Series': '/series',
      'Best Rated Shorts': '/shorts',
      'Best Rated Books': '/books',
      'Best Rated Comics': '/comics',
      // Latest
      'Latest Movies': '/movies',
      'Latest Series': '/series',
      'Latest Shorts': '/shorts',
      'Latest Books': '/books',
      'Latest Comics': '/comics',
      // Hidden Gems
      'Hidden Gems Movies': '/movies',
      'Hidden Gems Series': '/series',
      'Hidden Gems Shorts': '/shorts',
      'Hidden Gems Books': '/books',
      'Hidden Gems Comics': '/comics'
    };
    navigate(routeMap[title] || '/search');
  };

  return (
    <VStack
      gap={{ base: 4, sm: 6, md: 8 }}
      align="stretch"
      maxW="100%"
      w="100%"
      ml={0}
      mr={0}
      px={{ base: 2, sm: 3, md: 4, lg: 5 }}
      spacing={0}
      pt={0}
      mt={0}
      mb={0}
      pb={0}
    >
      {isLoading ? (
        <Center minH="50vh">
          <Spinner size="xl" color="purple.400" />
        </Center>
      ) : (
        <>
          <HeroBanner fullWidth={false} />

          {!user && (
            <Box
              bg={sectionBg}
              border="1px solid"
              borderColor={sectionBorderColor}
              borderRadius="xl"
              p={{ base: 6, md: 8 }}
              mx="auto"
              w="100%"
              maxW="1200px"
              boxShadow="md"
            >
              <VStack align="stretch" spacing={6}>
                <VStack align="start" spacing={2}>
                  <Text fontSize={{ base: '2xl', md: '3xl' }} fontWeight="bold" color={sectionTextColor}>
                    What is Vindie Stream?
                  </Text>
                  <Text fontSize={{ base: 'md', md: 'lg' }} color={sectionLabelColor} lineHeight="tall">
                    Vindie Stream is a platform where independent creators share their original content and viewers discover unique stories. Whether you're a creator or a viewer, you're welcome here.
                  </Text>
                </VStack>
                <Box
                  display="grid"
                  gridTemplateColumns={{ base: '1fr', md: 'repeat(2, 1fr)' }}
                  gap={6}
                >
                  <VStack align="start" spacing={3} p={4} bg={cardBg} borderRadius="lg">
                    <Text fontSize="xl" fontWeight="semibold" color={sectionTextColor}>
                      For Viewers
                    </Text>
                    <VStack align="start" spacing={2} fontSize="sm" color={sectionLabelColor}>
                      <Text>• Watch movies, series, shorts, books, and comics</Text>
                      <Text>• Discover hidden gems from independent creators</Text>
                      <Text>• Support creators with subscriptions and tips</Text>
                      <Text>• Download content to watch offline</Text>
                    </VStack>
                  </VStack>
                  <VStack align="start" spacing={3} p={4} bg={cardBg} borderRadius="lg">
                    <Text fontSize="xl" fontWeight="semibold" color={sectionTextColor}>
                      For Creators
                    </Text>
                    <VStack align="start" spacing={2} fontSize="sm" color={sectionLabelColor}>
                      <Text>• Upload and share your original content</Text>
                      <Text>• Build your audience and grow your channel</Text>
                      <Text>• Earn from subscriptions and tips</Text>
                      <Text>• Get analytics and insights on your content</Text>
                    </VStack>
                  </VStack>
                </Box>
                <HStack justify="center" spacing={4} pt={2}>
                  <Button
                    colorScheme="purple"
                    size="md"
                    onClick={() => navigate('/signup')}
                  >
                    Create Free Account
                  </Button>
                  <Button
                    variant="outline"
                    colorScheme="purple"
                    size="md"
                    onClick={() => navigate('/search')}
                  >
                    Start Browsing
                  </Button>
                </HStack>
              </VStack>
            </Box>
          )}

          {!isAdFreeLoading && !isAdFree && (
            <>
              <AdBanner type="banner" />
              <AdSenseSlot
                client={ADSENSE_CLIENT_ID}
                slot={ADSENSE_SLOTS.HOME_BANNER}
                format="auto"
                fullWidthResponsive
                style={{ margin: '8px 0' }}
              />
            </>
          )}

          {isContentEmpty ? (
            <Center minH="260px" border="1px dashed" borderRadius="2xl" bg={emptyStateBg}>
              <VStack spacing={4} textAlign="center" maxW="420px" px={5} py={2}>
                <Text fontSize="2xl" fontWeight="bold" color={titleColor}>
                  Content Not Found
                </Text>
                <Text fontSize="md" color={labelColor}>
                  We can’t find any content yet. Start exploring other categories or upload your own creation to kick things off.
                </Text>
                <Stack
                  direction={{ base: 'column', sm: 'row' }}
                  spacing={{ base: 2, sm: 3 }}
                  w="100%"
                >
                  <Button
                    leftIcon={
                      <Box
                        display="inline-flex"
                        alignItems="center"
                        justifyContent="center"
                        boxSize={{ base: '18px', sm: '20px' }}
                      >
                        <RepeatIcon boxSize="100%" />
                      </Box>
                    }
                    onClick={() => window.location.reload()}
                    colorScheme="purple"
                    size={{ base: 'md', sm: 'lg' }}
                    fontWeight="semibold"
                    borderRadius="lg"
                    py={{ base: 2.5, sm: 3 }}
                    w={{ base: '100%', sm: 'auto' }}
                  >
                    Refresh
                  </Button>
                  <Button
                    leftIcon={
                      <Box
                        display="inline-flex"
                        alignItems="center"
                        justifyContent="center"
                        boxSize={{ base: '18px', sm: '20px' }}
                      >
                        <ArrowUpIcon boxSize="100%" />
                      </Box>
                    }
                    variant="outline"
                    colorScheme="purple"
                    size={{ base: 'md', sm: 'lg' }}
                    fontWeight="semibold"
                    borderRadius="lg"
                    py={{ base: 2.5, sm: 3 }}
                    onClick={() => navigate('/upload/category')}
                    w={{ base: '100%', sm: 'auto' }}
                  >
                    Upload Content
                  </Button>
                </Stack>
              </VStack>
            </Center>
          ) : (
            contentRows.map((row, index) => (
              <ContentRow
                key={index}
                title={row.title}
                items={row.items}
                onSeeAll={() => handleSeeAll(row.title)}
                isContinueWatching={row.isContinueWatching}
                isSubscribed={row.isSubscribed}
              />
            ))
          )}
        </>
      )}
    </VStack>
  );
};

export default Home;
